import React, { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useOnboardingStore } from '../../store';
import { ArrowLeft, AlertCircle, FastForward } from 'lucide-react';
import type { Client } from '../../types';
import { useAuth } from '../../contexts/useAuth';
import { useNavigate } from 'react-router-dom';
import { sanitizeInput, validateEmail, validatePhone } from '../../lib/validation';
import { canSkipSteps, BETA_MODE } from '../../config/beta';
import { isDeveloperMode, devModeData } from '../../config/devMode';
import { saveOwnOnboardingProfile } from '../../lib/onboardingProfile';

export function BusinessInfo() {
  const { register, handleSubmit, formState: { errors }, setValue, control } = useForm<Partial<Client>>({
    defaultValues: isDeveloperMode ? {
      businessName: devModeData.business.businessName,
      contactName: devModeData.account.fullName,
      email: devModeData.account.email,
      phone: devModeData.account.phone,
      address: '123 Main St, San Francisco, CA 94102'
    } : {}
  });
  const { updateClient, client, setStep } = useOnboardingStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const watchedBusinessFields = useWatch({ control });

  // Pre-fill form with existing data
  React.useEffect(() => {
    if (client) {
      Object.entries(client).forEach(([key, value]) => {
        if (value) {
          setValue(key as keyof Client, value);
        }
      });
    }
  }, [client, setValue]);

  React.useEffect(() => {
    const hasWatchedValue = [
      watchedBusinessFields.businessName,
      watchedBusinessFields.contactName,
      watchedBusinessFields.email,
      watchedBusinessFields.phone,
      watchedBusinessFields.address
    ].some((value) => value !== undefined);
    if (!hasWatchedValue) return;

    updateClient({
      businessName: sanitizeInput(watchedBusinessFields.businessName || ''),
      contactName: sanitizeInput(watchedBusinessFields.contactName || ''),
      email: watchedBusinessFields.email ? sanitizeInput(watchedBusinessFields.email.toLowerCase().trim()) : '',
      phone: sanitizeInput(watchedBusinessFields.phone || ''),
      address: sanitizeInput(watchedBusinessFields.address || '')
    });
  }, [
    watchedBusinessFields.businessName,
    watchedBusinessFields.contactName,
    watchedBusinessFields.email,
    watchedBusinessFields.phone,
    watchedBusinessFields.address,
    updateClient
  ]);

  const handleSkip = () => {
    if (canSkipSteps()) {
      updateClient(BETA_MODE.mockData.client);
      setStep(1);
    }
  };

  const onSubmit = async (data: Partial<Client>) => {
    try {
      setIsLoading(true);
      setError(null);

      // Sanitize all inputs
      const sanitizedData = {
        businessName: sanitizeInput(data.businessName || ''),
        contactName: sanitizeInput(data.contactName || ''),
        email: data.email ? sanitizeInput(data.email.toLowerCase().trim()) : '',
        phone: sanitizeInput(data.phone || ''),
        address: sanitizeInput(data.address || '')
      };

      // Validate email if provided
      if (sanitizedData.email && !validateEmail(sanitizedData.email)) {
        throw new Error('Invalid email address');
      }

      // Validate phone if provided
      if (sanitizedData.phone && !validatePhone(sanitizedData.phone)) {
        throw new Error('Invalid phone number');
      }

      if (user?.email && sanitizedData.email !== user.email.toLowerCase()) {
        throw new Error('Use the email address associated with your signed-in account');
      }

      // Update the store with sanitized data
      updateClient(sanitizedData);

      // If user is already logged in, update their profile
      if (user) {
        await saveOwnOnboardingProfile({
          businessName: sanitizedData.businessName,
          contactName: sanitizedData.contactName,
          phone: sanitizedData.phone,
          address: sanitizedData.address,
          onboardingStep: 1,
        });
      }

      // Navigate to account creation step (step 1)
      setStep(1);

    } catch (err) {
      console.error('Business info error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while saving business information');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold text-white mb-2 text-center">Tell us about your business</h2>
      <p className="mb-6 text-center text-sm leading-6 text-gray-300">
        Start with what you know. This saves in the background and follows you into Connect and the dashboard tracker.
      </p>
      
      {error && (
        <div className="w-full mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="w-full space-y-6">
        <div>
          <label htmlFor="onboarding-business-name" className="block text-sm font-medium text-white mb-1">
            Business Name *
          </label>
          <input
            id="onboarding-business-name"
            type="text"
            {...register('businessName', { required: 'Business name is required' })}
            required
            autoComplete="organization"
            aria-invalid={Boolean(errors.businessName)}
            aria-describedby={errors.businessName ? 'onboarding-business-name-error' : undefined}
            className="mt-1 block w-full rounded-md bg-scale-darker border-gray-700 text-gray-100 placeholder-gray-500 focus:border-scale-blue focus:ring-scale-blue"
            placeholder="Enter your business name"
          />
          {errors.businessName && (
            <p id="onboarding-business-name-error" role="alert" className="mt-1 text-sm text-red-400">{errors.businessName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="onboarding-contact-name" className="block text-sm font-medium text-white mb-1">
            Contact Name *
          </label>
          <input
            id="onboarding-contact-name"
            type="text"
            {...register('contactName', { required: 'Contact name is required' })}
            required
            autoComplete="name"
            aria-invalid={Boolean(errors.contactName)}
            aria-describedby={errors.contactName ? 'onboarding-contact-name-error' : undefined}
            className="mt-1 block w-full rounded-md bg-scale-darker border-gray-700 text-gray-100 placeholder-gray-500 focus:border-scale-blue focus:ring-scale-blue"
            placeholder="Enter your full name"
          />
          {errors.contactName && (
            <p id="onboarding-contact-name-error" role="alert" className="mt-1 text-sm text-red-400">{errors.contactName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="onboarding-email" className="block text-sm font-medium text-white mb-1">
            Email Address *
          </label>
          <input
            id="onboarding-email"
            type="email"
            {...register('email', { 
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
            required
            autoComplete="email"
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'onboarding-email-error' : undefined}
            className="mt-1 block w-full rounded-md bg-scale-darker border-gray-700 text-gray-100 placeholder-gray-500 focus:border-scale-blue focus:ring-scale-blue"
            placeholder="Enter your email address"
          />
          {errors.email && (
            <p id="onboarding-email-error" role="alert" className="mt-1 text-sm text-red-400">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="onboarding-phone" className="block text-sm font-medium text-white mb-1">
            Phone Number *
          </label>
          <input
            id="onboarding-phone"
            type="tel"
            {...register('phone', { required: 'Phone number is required' })}
            required
            autoComplete="tel"
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={errors.phone ? 'onboarding-phone-error' : undefined}
            className="mt-1 block w-full rounded-md bg-scale-darker border-gray-700 text-gray-100 placeholder-gray-500 focus:border-scale-blue focus:ring-scale-blue"
            placeholder="Enter your phone number"
          />
          {errors.phone && (
            <p id="onboarding-phone-error" role="alert" className="mt-1 text-sm text-red-400">{errors.phone.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="onboarding-address" className="block text-sm font-medium text-white mb-1">
            Business Address *
          </label>
          <textarea
            id="onboarding-address"
            {...register('address', { required: 'Address is required' })}
            required
            autoComplete="street-address"
            aria-invalid={Boolean(errors.address)}
            aria-describedby={errors.address ? 'onboarding-address-error' : undefined}
            rows={3}
            className="mt-1 block w-full rounded-md bg-scale-darker border-gray-700 text-gray-100 placeholder-gray-500 focus:border-scale-blue focus:ring-scale-blue"
            placeholder="Enter your business address"
          />
          {errors.address && (
            <p id="onboarding-address-error" role="alert" className="mt-1 text-sm text-red-400">{errors.address.message}</p>
          )}
        </div>

        <div className="flex justify-between items-center pt-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>

          <div className="flex gap-3">
            {canSkipSteps() && (
              <button
                type="button"
                onClick={handleSkip}
                className="flex items-center bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-colors font-medium"
              >
                <FastForward className="w-4 h-4 mr-2" />
                Skip (Beta)
              </button>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center bg-scale-blue text-scale-dark px-6 py-2 rounded-md hover:bg-white focus:outline-none focus:ring-2 focus:ring-scale-blue focus:ring-offset-2 focus:ring-offset-scale-dark transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-scale-dark" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save & Continue'
            )}
          </button>
        </div>
        </div>
      </form>

      <p className="text-sm text-gray-400 mt-4 text-center">
        * Required fields
      </p>
    </div>
  );
}
